import { pdkAxios } from '@/lib/pdk-axios.ts';
import { db } from '@/models/db';
import type {
  DeltaPacketRecord,
  FullHydrateResponse,
} from '@/models/hydrate.ts';
import { Seller } from '@/models/registry.ts';
import type {
  BaseStore,
  StoreHydrationDelegator,
} from '@/models/stores/base.ts';
import type { Seller as SellerType } from '@/models/types.ts';
import { makeAutoObservable, observable, type ObservableMap } from 'mobx';

class SellerStore implements BaseStore<Seller, SellerType> {
  store: ObservableMap<string, Seller> = observable.map();

  constructor() {
    makeAutoObservable(this, { store: observable.shallow }, { autoBind: true });
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<SellerType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<SellerType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.sellers.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.store.delete(delta.record_id);
      return;
    }

    this.upsert(delta.data);
  }

  get all() {
    return Array.from(this.store.values());
  }

  getById(id: string): Seller | undefined {
    return this.store.get(id);
  }

  add(input: SellerType) {
    const seller = new Seller(input);
    this.store.set(seller.id, seller);
  }

  save(seller: Seller) {
    this.store.delete(seller.id);
    this.store.set(seller.id, seller);
  }

  upsert(seller: SellerType) {
    // TODO -- temp fix, shouldnt be explicit casting id to string
    const idToUse = String(seller.id);
    const existingSeller = this.getById(idToUse);
    if (existingSeller) {
      this.replace({ id: idToUse, seller });
    } else {
      this.add(seller);
    }
  }

  replace({ id, seller }: { id: string; seller: SellerType }) {
    const existingSeller = this.getById(id);
    if (!existingSeller) return;
    existingSeller.first_name = seller.first_name;
    existingSeller.last_name = seller.last_name;
    existingSeller.middle_name = seller.middle_name;
    existingSeller.phone_number = seller.phone_number;
    existingSeller.email = seller.email;

    this.save(existingSeller);
  }
}

export const sellerStore = new SellerStore();

// TODO -- currently doing full hydrate every time, since sellers cant be tracked via conventional delta sync
class SellerStoreHydrationDelegator
  implements StoreHydrationDelegator<SellerType>
{
  async hydrate({
    fullHydrateEndpoint,
  }: {
    fullHydrateEndpoint: string;
    deltaSyncEndpoint: string | null;
  }) {
    const resp =
      await pdkAxios.get<FullHydrateResponse<SellerType>>(fullHydrateEndpoint);
    for (const seller of resp.data.data) {
      await db.sellers.put(seller, String(seller.id));
      sellerStore.upsert(seller);
    }
  }
}

export const sellerStoreHydrationDelegator =
  new SellerStoreHydrationDelegator();
